import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle, MapPin, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reservation status labels and styling
const PREORDER_STATUS_LABELS = {
  deposit_received: { label: 'Deposit Received', color: 'bg-amber-100 text-amber-700', description: 'We have received your deposit and your reservation is confirmed.' },
  partial_payment: { label: 'Partial Payment', color: 'bg-blue-100 text-blue-700', description: 'Additional payment received. Thank you!' },
  paid_in_full: { label: 'Paid in Full', color: 'bg-green-100 text-green-700', description: 'Your reservation is fully paid and being prepared.' },
  ready_to_ship: { label: 'Ready to Ship', color: 'bg-purple-100 text-purple-700', description: 'Your order is packed and ready to ship!' },
  shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-700', description: 'Your order is on its way!' },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', description: 'Your order has been delivered. Enjoy!' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', description: 'This reservation has been cancelled.' }
};

const TrackOrder = () => {
  const [trackingType, setTrackingType] = useState('preorder'); // 'preorder' or 'order'
  const [orderCode, setOrderCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pre-order state
  const [preorderData, setPreorderData] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  
  // Regular order state
  const [orderData, setOrderData] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!orderCode.trim() || !email.trim()) {
      toast.error('Please enter both your order code and email');
      return;
    }

    setLoading(true);
    setError('');
    setPreorderData(null);
    setOrderData(null);

    try {
      if (trackingType === 'preorder') {
        // Track pre-order
        const res = await axios.post(`${API_URL}/api/preorders/check-status`, {
          order_code: orderCode.trim().toUpperCase(),
          email: email.trim().toLowerCase()
        });
        setPreorderData(res.data);
        toast.success('Pre-order found!');
      } else {
        // Track regular order
        const res = await axios.get(`${API_URL}/api/orders/track/${orderCode.trim()}`, {
          params: { email: email.trim() }
        });
        setOrderData(res.data);
        toast.success('Order found!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Order not found. Please check your details and try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getPreorderStatusInfo = (status) => {
    return PREORDER_STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700', description: '' };
  };

  const resetForm = () => {
    setPreorderData(null);
    setOrderData(null);
    setOrderCode('');
    setEmail('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="pt-44 pb-16 section-padding">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
              <Package className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4">
              Track Your Order
            </h1>
            <p className="text-lg text-gray-600">
              Enter your order details below to check the status of your D-Freeze order or reservation.
            </p>
          </div>

          {/* Search Form */}
          {!preorderData && !orderData && (
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-8 animate-fade-in" data-testid="track-order-form">
              {/* Tracking Type Selector */}
              <div className="flex gap-2 mb-8 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => { setTrackingType('preorder'); setError(''); }}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    trackingType === 'preorder' 
                      ? 'bg-white text-[#003456] shadow-sm' 
                      : 'text-gray-600 hover:text-[#003456]'
                  }`}
                  data-testid="preorder-tab"
                >
                  <Package className="h-4 w-4 inline mr-2" />
                  Reservation
                </button>
                <button
                  type="button"
                  onClick={() => { setTrackingType('order'); setError(''); }}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    trackingType === 'order' 
                      ? 'bg-white text-[#003456] shadow-sm' 
                      : 'text-gray-600 hover:text-[#003456]'
                  }`}
                  data-testid="order-tab"
                >
                  <Truck className="h-4 w-4 inline mr-2" />
                  Regular Order
                </button>
              </div>

              <form onSubmit={handleTrack} className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {trackingType === 'preorder' ? 'Reservation Code' : 'Order ID'}
                  </label>
                  <Input
                    value={orderCode}
                    onChange={(e) => setOrderCode(trackingType === 'preorder' ? e.target.value.toUpperCase() : e.target.value)}
                    placeholder={trackingType === 'preorder' ? 'e.g., PO-ABC123' : 'Enter your order ID'}
                    className={`input-focus ${trackingType === 'preorder' ? 'font-mono uppercase' : ''}`}
                    data-testid="order-code-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {trackingType === 'preorder' 
                      ? 'Found in your reservation confirmation email' 
                      : 'Found in your order confirmation email'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Email Address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter the email used for your order"
                    className="input-focus"
                    data-testid="email-input"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Order Not Found</p>
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-6 text-lg"
                  data-testid="track-order-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" /> Track {trackingType === 'preorder' ? 'Reservation' : 'Order'}
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Pre-Order Results */}
          {preorderData && (
            <div className="space-y-6 animate-fade-in" data-testid="preorder-result">
              {/* Success Header */}
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#0a0203] mb-2">Reservation Found!</h2>
                <p className="text-gray-600">Here are your reservation details</p>
              </div>

              {/* Order Code & Status */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Reservation Code</span>
                    <p className="font-mono text-2xl font-bold text-[#003456]" data-testid="preorder-code-display">
                      {preorderData.order_code}
                    </p>
                  </div>
                  <div className="text-left md:text-right">
                    <span className="text-sm text-gray-500">Status</span>
                    <div className={`mt-1 px-4 py-2 rounded-md text-sm font-medium inline-block ${getPreorderStatusInfo(preorderData.status).color}`}>
                      {getPreorderStatusInfo(preorderData.status).label}
                    </div>
                  </div>
                </div>
                {getPreorderStatusInfo(preorderData.status).description && (
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-blue-800">{getPreorderStatusInfo(preorderData.status).description}</p>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#0a0203] mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#003456]" /> Product
                </h3>
                <p className="text-lg text-gray-900" data-testid="preorder-product-name">{preorderData.product_name}</p>
                <p className="text-sm text-gray-500 mt-1">Ordered on {formatDate(preorderData.created_at)}</p>
                {preorderData.estimated_ship_date && (
                  <p className="text-sm text-amber-600 mt-2 font-medium flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Estimated Ship Date: {formatDate(preorderData.estimated_ship_date)}
                  </p>
                )}
              </div>

              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-[#003456] to-[#002B47] rounded-2xl p-6 shadow-sm text-white">
                <h3 className="font-semibold mb-4 text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> Payment Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/80">Total Price</span>
                    <span className="font-mono font-medium" data-testid="preorder-total">
                      {formatCurrency(preorderData.total_price)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Amount Paid</span>
                    <span className="font-mono font-medium text-green-400" data-testid="preorder-paid">
                      {formatCurrency(preorderData.amount_paid)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-white/20">
                    <span className="font-semibold text-lg">Balance Owing</span>
                    <span className={`font-mono font-bold text-xl ${preorderData.balance_owing > 0 ? 'text-amber-400' : 'text-green-400'}`} data-testid="preorder-balance">
                      {formatCurrency(preorderData.balance_owing)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Balance Alert */}
              {preorderData.balance_owing > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800">
                    <strong>Payment Required:</strong> You have a remaining balance of {formatCurrency(preorderData.balance_owing)}. 
                    We'll contact you when your order is ready to ship to arrange final payment.
                  </p>
                </div>
              )}

              {/* Payment History */}
              {preorderData.payment_history && preorderData.payment_history.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <button
                    onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="toggle-history-btn"
                  >
                    <span className="font-semibold text-[#0a0203]">
                      Payment History ({preorderData.payment_history.length} payments)
                    </span>
                    {showPaymentHistory ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                  </button>
                  
                  {showPaymentHistory && (
                    <div className="mt-4 space-y-3" data-testid="payment-history-list">
                      {preorderData.payment_history.map((payment, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-xl p-4 flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">{payment.type}</p>
                            <p className="text-sm text-gray-500">{formatDate(payment.date)}</p>
                            {payment.notes && <p className="text-xs text-gray-400 mt-1">{payment.notes}</p>}
                          </div>
                          <span className="font-mono font-medium text-green-600">+{formatCurrency(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Track Another Order */}
              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full border-2 border-dashed py-6"
                data-testid="track-another-btn"
              >
                Track Another Order
              </Button>
            </div>
          )}

          {/* Regular Order Results */}
          {orderData && (
            <div className="space-y-6 animate-fade-in" data-testid="order-result">
              {/* Order Summary */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0a0203]">
                      Order #{orderData.id?.slice(0, 8)}...
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Placed on {formatDate(orderData.created_at)}
                    </p>
                  </div>
                  <span className={`px-4 py-2 rounded-md text-sm font-medium ${
                    orderData.payment_status === 'paid' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {orderData.payment_status === 'paid' ? 'Payment Confirmed' : 'Payment Pending'}
                  </span>
                </div>

                {/* Status Timeline */}
                {orderData.timeline && (
                  <div className="relative">
                    <div className="absolute left-[23px] top-8 bottom-8 w-0.5 bg-gray-200" />
                    <div className="space-y-6">
                      {orderData.timeline.map((step, index) => (
                        <div key={step.status} className="flex items-center gap-4 relative">
                          <div className={`z-10 flex items-center justify-center w-12 h-12 rounded-full ${
                            step.completed ? 'bg-[#dbeef5]' : 'bg-gray-100'
                          }`}>
                            {step.completed ? (
                              <CheckCircle className="h-6 w-6 text-[#003456]" />
                            ) : (
                              <Clock className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-semibold text-base ${step.completed ? 'text-[#0a0203]' : 'text-gray-500'}`}>
                              {step.label}
                            </p>
                            {step.date && (
                              <p className="text-sm text-gray-500 mt-0.5">{step.date}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Items */}
              {orderData.items && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#0a0203] mb-4">Order Items</h3>
                  <div className="space-y-4">
                    {orderData.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0a0203]">{item.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                          ${(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="font-semibold text-[#0a0203]">Total</span>
                    <span className="text-2xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${orderData.total?.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              {orderData.shipping_address && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#0a0203] mb-4">Shipping Address</h3>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-5 w-5 text-[#003456] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0a0203]">{orderData.customer_name}</p>
                      <p className="text-gray-600">{orderData.shipping_address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Track Another Order */}
              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full border-2 border-dashed py-6"
                data-testid="track-another-btn"
              >
                Track Another Order
              </Button>
            </div>
          )}

          {/* Help Section */}
          {!preorderData && !orderData && (
            <div className="grid md:grid-cols-3 gap-6 animate-fade-in">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="h-6 w-6 text-[#003456]" />
                </div>
                <h3 className="font-semibold text-[#0a0203] mb-2">Where's my order code?</h3>
                <p className="text-sm text-gray-600">
                  Your order code was sent to your email when you placed your order.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-6 w-6 text-[#003456]" />
                </div>
                <h3 className="font-semibold text-[#0a0203] mb-2">Can't find your email?</h3>
                <p className="text-sm text-gray-600">
                  Check your spam folder, or contact us and we'll resend your order details.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-6 w-6 text-[#003456]" />
                </div>
                <h3 className="font-semibold text-[#0a0203] mb-2">Need help?</h3>
                <p className="text-sm text-gray-600">
                  <Link to="/contact" className="text-[#003456] font-medium hover:underline">Contact support</Link> for assistance.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TrackOrder;
